/**
 * Block models of the redstone components (cuboids in 1/16 units, like
 * models.ts). `redstoneQuads` returns null for ids it does not draw (cubes,
 * torches: those go through the ordinary paths).
 */
import { BLOCKS, T } from '../../blocks';
import { orientedQuad, type Quad } from '../../entityModels';
import { K, RS_DIR, RS_FLAG, RS_KIND, RS_POW, RS_VAR } from './defs';
import { dustSides } from './power';

export interface RsNb {
  px: number; nx: number; pz: number; nz: number;
  /** Any neighbour (the mesher's padded chunk); dust needs the diagonals. */
  at?(dx: number, dy: number, dz: number): number;
}

type V3 = readonly number[];
/** [u0, v0, u1, v1] in 1/16 of the tile, for the four side faces / the top and bottom. */
type UV = readonly [number, number, number, number];

/** A cuboid; faces take position-derived UVs unless `side` / `cap` rectangles are given. */
function box(min: V3, max: V3, tiles: readonly number[], side?: UV, cap?: UV): Quad[] {
  const lo = min.map((v) => v / 16), hi = max.map((v) => v / 16);
  const out: Quad[] = [];
  for (let a = 0; a < 3; a++) {
    const b = (a + 1) % 3, c = (a + 2) % 3;
    for (const s of [1, 0]) {
      const corners = [[0, 0], [1, 0], [1, 1], [0, 1]];
      const pts = corners.map(([i, j]) => {
        const p = [0, 0, 0];
        p[a] = s ? hi[a] : lo[a]; p[b] = i ? hi[b] : lo[b]; p[c] = j ? hi[c] : lo[c];
        return p;
      });
      const rect = a === 1 ? cap : side;
      const uvs = pts.map((p): number[] => {
        if (rect) {
          // horizontal position across the face -> u, height (or depth on caps) -> v
          const h = a === 1 ? (p[0] - lo[0]) / (hi[0] - lo[0] || 1) : a === 0 ? (p[2] - lo[2]) / (hi[2] - lo[2] || 1) : (p[0] - lo[0]) / (hi[0] - lo[0] || 1);
          const t = a === 1 ? (p[2] - lo[2]) / (hi[2] - lo[2] || 1) : 1 - (p[1] - lo[1]) / (hi[1] - lo[1] || 1);
          return [(rect[0] + (rect[2] - rect[0]) * h) / 16, (rect[1] + (rect[3] - rect[1]) * t) / 16];
        }
        return a === 1 ? [p[0], p[2]] : a === 0 ? [s ? 1 - p[2] : p[2], 1 - p[1]] : [s ? p[0] : 1 - p[0], 1 - p[1]];
      });
      const n = [0, 0, 0];
      n[a] = s ? 1 : -1;
      out.push(orientedQuad(pts, uvs, tiles[a * 2 + (s ? 0 : 1)], n));
    }
  }
  return out;
}
const all = (t: number): number[] => [t, t, t, t, t, t];

/** Move every corner through `f` (a rotation of the cell) and re-derive the face from the winding. */
function xform(quads: Quad[], f: (x: number, y: number, z: number) => number[]): Quad[] {
  return quads.map((q) => {
    const pos: number[] = [];
    for (let k = 0; k < 4; k++) pos.push(...f(q.pos[k * 3], q.pos[k * 3 + 1], q.pos[k * 3 + 2]));
    const ax = pos[3] - pos[0], ay = pos[4] - pos[1], az = pos[5] - pos[2];
    const bx = pos[6] - pos[3], by = pos[7] - pos[4], bz = pos[8] - pos[5];
    const n = [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    let face = 6;
    for (let a = 0; a < 3; a++) { if (n[a] / len > 0.99) face = a * 2; else if (n[a] / len < -0.99) face = a * 2 + 1; }
    return { ...q, pos, face };
  });
}
/** A model built standing on the floor, turned to hang on the neighbour in direction `d`. */
function attach(quads: Quad[], d: number): Quad[] {
  switch (d) {
    case 0: return xform(quads, (x, y, z) => [1 - y, x, z]);
    case 1: return xform(quads, (x, y, z) => [y, 1 - x, z]);
    case 2: return xform(quads, (x, y, z) => [x, 1 - y, 1 - z]);
    case 4: return xform(quads, (x, y, z) => [x, z, 1 - y]);
    case 5: return xform(quads, (x, y, z) => [x, 1 - z, y]);
    default: return quads;
  }
}
/** A model built with its signal running towards -Z, turned to run towards horizontal `d`. */
function turn(quads: Quad[], d: number): Quad[] {
  switch (d) {
    case 0: return xform(quads, (x, y, z) => [1 - z, y, x]);
    case 4: return xform(quads, (x, y, z) => [1 - x, y, 1 - z]);
    case 1: return xform(quads, (x, y, z) => [z, y, 1 - x]);
    default: return quads;
  }
}

/** Vanilla's wire colour for a power level. */
function dustTint(p: number): [number, number, number] {
  const f = p / 15;
  const r = f * 0.6 + (p > 0 ? 0.4 : 0.3);
  const g = Math.max(0, f * f * 0.7 - 0.5);
  const b = Math.max(0, f * f * 0.6 - 0.7);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function dustQuads(mask: number, power: number): Quad[] {
  const tint = dustTint(power);
  const y = 0.25 / 16, out: Quad[] = [];
  const flat = (x0: number, z0: number, x1: number, z1: number, tile: number, alongX: boolean): void => {
    const pts = [[x0, y, z0], [x1, y, z0], [x1, y, z1], [x0, y, z1]];
    const uvs = pts.map((p) => (alongX ? [p[2], p[0]] : [p[0], p[2]]));
    out.push({ ...orientedQuad(pts, uvs, tile, [0, 1, 0]), tint });
  };
  const sides = mask & 0b110011;
  if (sides !== 0b000011 && sides !== 0b110000) flat(0, 0, 1, 1, T.RS_DUST_DOT, false);
  if (sides & 1) flat(0.5, 0, 1, 1, T.RS_DUST_LINE, true);
  if (sides & 2) flat(0, 0, 0.5, 1, T.RS_DUST_LINE, true);
  if (sides & 16) flat(0, 0.5, 1, 1, T.RS_DUST_LINE, false);
  if (sides & 32) flat(0, 0, 1, 0.5, T.RS_DUST_LINE, false);
  // the run up the side of a block
  const e = 0.25 / 16;
  const wall = (pts: number[][], n: number[]): void => {
    const uvs = pts.map((p) => [n[0] !== 0 ? p[2] : p[0], 1 - p[1]]);
    out.push({ ...orientedQuad(pts, uvs, T.RS_DUST_LINE, n), tint });
  };
  if (mask & (1 << 8)) wall([[1 - e, 0, 0], [1 - e, 0, 1], [1 - e, 1, 1], [1 - e, 1, 0]], [-1, 0, 0]);
  if (mask & (1 << 9)) wall([[e, 0, 0], [e, 0, 1], [e, 1, 1], [e, 1, 0]], [1, 0, 0]);
  if (mask & (1 << 12)) wall([[0, 0, 1 - e], [1, 0, 1 - e], [1, 1, 1 - e], [0, 1, 1 - e]], [0, 0, -1]);
  if (mask & (1 << 13)) wall([[0, 0, e], [1, 0, e], [1, 1, e], [0, 1, e]], [0, 0, 1]);
  return out;
}

const cache = new Map<number, readonly Quad[]>();
const dustCache = new Map<number, readonly Quad[]>();
const TORCH_SIDE: UV = [7, 6, 9, 11], TORCH_CAP: UV = [7, 6, 9, 8];
const torch = (x: number, z: number, h: number, lit: boolean): Quad[] =>
  box([x, 2, z], [x + 2, 2 + h, z + 2], all(lit ? T.RS_TORCH : T.RS_TORCH_OFF), TORCH_SIDE, TORCH_CAP);

function build(id: number, nb: RsNb): Quad[] | null {
  const tiles = BLOCKS[id].tiles, dir = RS_DIR[id], pow = RS_POW[id], variant = RS_VAR[id];
  switch (RS_KIND[id]) {
    case K.LEVER: {
      const q = box([5, 0, 4], [11, 3, 12], all(T.COBBLE));
      q.push(...box([7, 3, pow ? 9 : 5], [9, 13, pow ? 11 : 7], all(T.LEVER), [7, 6, 9, 16], [7, 6, 9, 8]));
      return attach(q, dir);
    }
    case K.BUTTON: return attach(box([5, 0, 6], [11, pow ? 1 : 2, 10], tiles), dir);
    case K.PLATE: return box([1, 0, 1], [15, pow ? 0.5 : 1, 15], tiles);
    case K.REPEATER: {
      const q = box([0, 0, 0], [16, 2, 16], [T.SMOOTH_STONE, T.SMOOTH_STONE, tiles[2], T.SMOOTH_STONE, T.SMOOTH_STONE, T.SMOOTH_STONE]);
      q.push(...torch(7, 2, 5, pow === 1));
      if (RS_FLAG[id] === 1) q.push(...box([2, 2, 6 + variant * 2], [14, 4, 8 + variant * 2], all(T.BEDROCK)));
      else q.push(...torch(7, 6 + variant * 2, 5, pow === 1));
      return turn(q, dir);
    }
    case K.COMPARATOR: {
      const q = box([0, 0, 0], [16, 2, 16], [T.SMOOTH_STONE, T.SMOOTH_STONE, tiles[2], T.SMOOTH_STONE, T.SMOOTH_STONE, T.SMOOTH_STONE]);
      q.push(...torch(4, 11, 5, pow > 0), ...torch(10, 11, 5, pow > 0), ...torch(7, 2, variant ? 4 : 2, variant === 1));
      return turn(q, dir);
    }
    // built facing up (piston_side.png: head in rows 0..4, body below), then turned
    case K.PISTON: {
      const s = T.PISTON_SIDE, face = variant ? T.PISTON_TOP_STICKY : T.PISTON_TOP;
      const q = RS_FLAG[id] === 1
        ? box([0, 0, 0], [16, 12, 16], [s, s, T.PISTON_INNER, T.PISTON_BOTTOM, s, s])
        : box([0, 0, 0], [16, 16, 16], [s, s, face, T.PISTON_BOTTOM, s, s]);
      return attach(q, dir ^ 1);
    }
    case K.HEAD: {
      const s = T.PISTON_SIDE, face = variant ? T.PISTON_TOP_STICKY : T.PISTON_TOP;
      const q = box([0, 12, 0], [16, 16, 16], [s, s, face, T.PISTON_TOP, s, s]);
      q.push(...box([6, -4, 6], [10, 12, 10], all(s), [0, 0, 16, 4], [6, 0, 10, 4]));
      return attach(q, dir ^ 1);
    }
    case K.DAYLIGHT: return box([0, 0, 0], [16, 6, 16], tiles);
    case K.HOOK: {
      const q = box([6, 0, 4], [10, 2, 12], all(T.PLANKS));
      q.push(...box([7, 2, 7], [9, pow ? 5 : 8, 9], all(T.TRIPWIRE_HOOK), [7, 4, 9, 10], [7, 4, 9, 6]));
      return attach(q, dir);
    }
    case K.TRIPWIRE: {
      const isWire = (n: number): boolean => RS_KIND[n] === K.TRIPWIRE || RS_KIND[n] === K.HOOK;
      const alongX = isWire(nb.px) || isWire(nb.nx);
      // vanilla: a half-pixel strip, the first two rows of tripwire.png
      const y = (pow ? 0.5 : 1.5) / 16, a = 7.75 / 16, b = 8.25 / 16;
      const pts = alongX ? [[0, y, a], [1, y, a], [1, y, b], [0, y, b]] : [[a, y, 0], [b, y, 0], [b, y, 1], [a, y, 1]];
      const uvs = pts.map((p) => (alongX ? [p[0], (p[2] - a) / (b - a) / 8] : [p[2], (p[0] - a) / (b - a) / 8]));
      return [orientedQuad(pts, uvs, T.TRIPWIRE, [0, 1, 0]), orientedQuad(pts, uvs, T.TRIPWIRE, [0, -1, 0])];
    }
    default: return null;
  }
}

const NONE: readonly Quad[] = [];
export function redstoneQuads(id: number, nb: RsNb): readonly Quad[] | null {
  const kind = RS_KIND[id];
  if (kind === K.NONE) return null;
  if (kind === K.DUST) {
    const at = nb.at;
    const mask = at ? dustSides({ getBlock: (x, y, z) => at(x, y, z) }, 0, 0, 0) : 0b110011;
    const key = mask * 16 + RS_POW[id];
    let q = dustCache.get(key);
    if (!q) { q = dustQuads(mask, RS_POW[id]); dustCache.set(key, q); }
    return q;
  }
  const key = kind === K.TRIPWIRE ? id * 2 + (RS_KIND[nb.px] === K.TRIPWIRE || RS_KIND[nb.px] === K.HOOK || RS_KIND[nb.nx] === K.TRIPWIRE || RS_KIND[nb.nx] === K.HOOK ? 1 : 0) : id * 2;
  let q = cache.get(key);
  if (q === undefined) { q = build(id, nb) ?? NONE; cache.set(key, q); }
  return q;
}
